import { clearAuthSession } from './session'
import { useAuthStore } from 'shared/store/auth'

type TelegramRefreshHandler = () => Promise<string>

let telegramRefreshHandler: TelegramRefreshHandler | null = null
let telegramRefreshInFlight: Promise<string> | null = null

export function registerTelegramRefreshHandler(
	handler: TelegramRefreshHandler,
): void {
	telegramRefreshHandler = handler
}

export function refreshTelegramSession(): Promise<string> {
	if (!telegramRefreshHandler) {
		return Promise.reject(new Error('Telegram refresh handler is not registered'))
	}
	if (!telegramRefreshInFlight) {
		telegramRefreshInFlight = telegramRefreshHandler().finally(() => {
			telegramRefreshInFlight = null
		})
	}
	return telegramRefreshInFlight
}

async function recoverUnauthorized(): Promise<string | null> {
	const mode = useAuthStore.getState().mode
	if (mode === 'telegram') {
		// Dev override sessions cannot be recovered via Telegram refresh —
		// they are impersonated users and have no corresponding initData.
		const cached = localStorage.getItem('@auth:telegram:v3')
		if (cached) {
			try {
				const parsed = JSON.parse(cached) as { isDevOverride?: boolean }
				if (parsed.isDevOverride) {
					clearAuthSession('telegram', 'Dev session expired')
					return null
				}
			} catch {
				// corrupted — fall through to normal flow
			}
		}
		try {
			return await refreshTelegramSession()
		} catch (error) {
			clearAuthSession(
				'telegram',
				error instanceof Error ? error.message : 'Authentication failed',
			)
			return null
		}
	}
	clearAuthSession(mode ?? 'browser-admin')
	return null
}

function isAuthRequest(input: RequestInfo | URL): boolean {
	const url = String(input)
	return (
		url.includes('auth.Login') ||
		url.includes('auth.TelegramLogin') ||
		url.includes('dev.Unlock') ||
		url.includes('dev.Login')
	)
}

export async function authenticatedFetch(
	input: RequestInfo | URL,
	init?: RequestInit,
): Promise<Response> {
	const response = await globalThis.fetch(input, {
		...init,
		credentials: 'include',
	})
	if (response.status !== 401 || isAuthRequest(input)) return response

	const token = await recoverUnauthorized()
	if (!token) return response

	const headers = new Headers(init?.headers)
	headers.set('authorization', `Bearer ${token}`)
	return globalThis.fetch(input, {
		...init,
		headers,
		credentials: 'include',
	})
}
